import * as React from 'react';

import { Button, TextField, ReMatterLogo, cn } from '@rematter/ui-web';
import { cva } from 'class-variance-authority';
import { useLocation } from 'wouter';

import { useAcceptUserInvitationMutation } from './mutationAcceptUserInvitation';

import { useTranslations } from '~/shared/i18n/useTranslations';

const containerVariants = cva([
  'relative flex min-h-screen items-center justify-center overflow-hidden p-5',
  'bg-[radial-gradient(circle_at_20%_30%,rgba(99,102,241,0.03)_0%,transparent_50%),radial-gradient(circle_at_80%_70%,rgba(99,102,241,0.03)_0%,transparent_50%),linear-gradient(45deg,rgba(99,102,241,0.01)_25%,transparent_25%,transparent_75%,rgba(99,102,241,0.01)_75%),linear-gradient(-45deg,rgba(99,102,241,0.01)_25%,transparent_25%,transparent_75%,rgba(99,102,241,0.01)_75%),linear-gradient(120deg,#f5f7ff_0%,#ffffff_100%)]',
  'bg-[length:100%_100%,100%_100%,60px_60px,60px_60px,100%_100%]',
]);

const cardVariants = cva([
  'relative z-10 flex w-full max-w-[400px] flex-col rounded-xl p-10 shadow-lg',
  'border border-white/20 bg-white/80 backdrop-blur-sm',
]);

export const AcceptInvitation = () => {
  const t = useTranslations();
  const [, setLocation] = useLocation();
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');

  const { mutateAsync: acceptInvitation, isPending } =
    useAcceptUserInvitationMutation();

  const token = React.useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('token');
  }, []);

  React.useEffect(() => {
    if (!token) {
      setError(t.auth.acceptInvitation.invalidLink);
    }
  }, [token, t]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (password !== confirmPassword) {
      setError(t.auth.acceptInvitation.passwordsDoNotMatch);
      return;
    }

    try {
      setError(null);

      const result = await acceptInvitation({
        token,
        password,
      });

      const response = result.authenticationAcceptUserInvitation;

      if (response.__typename === 'UserInvitationAcceptedResult') {
        setSuccess(t.auth.acceptInvitation.successMessage);
        setLocation('/sign-in');
      } else if (response.__typename === 'InvalidUserInvitationError') {
        setError(response.reason ?? t.auth.acceptInvitation.invalidInvitation);
      } else {
        setError(t.auth.acceptInvitation.failedToAccept);
      }
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(t.auth.acceptInvitation.processingError);
    }
  };

  const isFormValid =
    password.trim() !== '' &&
    confirmPassword.trim() !== '' &&
    password === confirmPassword;

  const renderContent = () => {
    if (error && !token) {
      return (
        <div className="flex flex-col space-y-4">
          <div className="rounded-lg bg-warning/10 p-4">
            <h3 className="text-sm font-medium text-warning">
              {t.auth.acceptInvitation.invitationError}
            </h3>
            <p className="mt-1 text-sm text-warning/80">{error}</p>
          </div>
          <Button
            variant="outline"
            onPress={() => setLocation('/sign-in')}
            className="w-full"
          >
            {t.auth.acceptInvitation.goToSignIn}
          </Button>
        </div>
      );
    }

    if (success) {
      return (
        <div className="flex flex-col space-y-4">
          <div className="rounded-lg bg-success/10 p-4">
            <h3 className="text-sm font-medium text-success">
              {t.auth.acceptInvitation.success}
            </h3>
            <p className="mt-1 text-sm text-success/80">{success}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-neutral/80">
            {t.auth.acceptInvitation.title}
          </h1>
          <p className="mt-2 text-sm text-neutral">
            {t.auth.acceptInvitation.subtitle}
          </p>
        </div>

        <form onSubmit={onSubmit}>
          <div className="space-y-4">
            <TextField
              type="password"
              label={t.auth.acceptInvitation.passwordLabel}
              placeholder={t.auth.acceptInvitation.passwordPlaceholder}
              value={password}
              onChange={setPassword}
              isDisabled={isPending}
              isRequired
            />

            <TextField
              type="password"
              label={t.auth.acceptInvitation.confirmPasswordLabel}
              placeholder={t.auth.acceptInvitation.confirmPasswordPlaceholder}
              value={confirmPassword}
              onChange={setConfirmPassword}
              isDisabled={isPending}
              isRequired
            />

            {error && (
              <div className="rounded-lg bg-warning/10 p-3">
                <p className="text-sm text-warning">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              isDisabled={!isFormValid || isPending}
              className="w-full"
            >
              {isPending
                ? t.auth.acceptInvitation.processing
                : t.auth.acceptInvitation.submitButton}
            </Button>
          </div>
        </form>

        <p className="text-center text-xs text-neutral/60">
          {t.auth.acceptInvitation.disclaimer}
        </p>
      </div>
    );
  };

  return (
    <div className={cn('@AcceptInvitation', containerVariants())}>
      <div className={cardVariants()}>
        <div className="mb-6 flex justify-center">
          <ReMatterLogo size="large" height={50} />
        </div>
        {renderContent()}
      </div>
    </div>
  );
};
